/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { ImportedReceipt, ReceiptSummary } from "./types";
import { ReceiptEditor } from "./ReceiptEditor";
import {
  ScanDialog,
  scanToReceipt,
  useScanRouting,
  type ScanResult,
} from "../Scan/index";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

// Goods receipts: what arrived from suppliers. Posting one adds stock.
// See docs/plans/inventory_and_costing/05_purchasing.md (#4).
export const InventoryPurchasesPage = defineComponent({
  name: "InventoryPurchasesPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const receipts = ref<ReceiptSummary[]>([]);
    // undefined = closed, null = new receipt, number = open that receipt.
    const editing = ref<number | null | undefined>(undefined);
    const initial = ref<ImportedReceipt | null>(null);
    const importing = ref(false);
    const fileInput = ref<HTMLInputElement | null>(null);

    // XRechnung / ZUGFeRD XML → a pre-filled draft in the receipt editor.
    const importFile = async (file: File) => {
      importing.value = true;
      try {
        const draft = await trpc.purchasing.importEInvoice.mutate({
          xml: await file.text(),
        });
        initial.value = draft as ImportedReceipt;
        editing.value = null;
      } catch (e) {
        toast.showToast(
          (e as { message?: string })?.message ??
            t("inventory.purchases.import_failed"),
          {
            type: "error",
          },
        );
      } finally {
        importing.value = false;
        if (fileInput.value) fileInput.value.value = "";
      }
    };

    const { scanning, onOpen } = useScanRouting(
      "receipt",
      (result: ScanResult) => {
        initial.value = scanToReceipt(result);
        editing.value = null;
      },
    );

    const load = async () => {
      receipts.value = (await trpc.purchasing.receipts.query({
        limit: 100,
        offset: 0,
      })) as ReceiptSummary[];
    };

    onMounted(async () => {
      try {
        await load();
      } catch {
        toast.showToast(t("inventory.purchases.load_failed"), {
          type: "error",
        });
      } finally {
        loading.value = false;
      }
    });

    const remove = async (receipt: ReceiptSummary) => {
      if (!confirm(t("inventory.purchases.delete_confirm"))) return;
      try {
        await trpc.purchasing.deleteReceipt.mutate({ id: receipt.id });
        await load();
      } catch {
        toast.showToast(t("inventory.purchases.delete_failed"), {
          type: "error",
        });
      }
    };

    const formatDate = (iso: string) => new Date(iso).toLocaleDateString();

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("inventory.nav.deliveries")}
          subtitle={t("inventory.purchases.subtitle")}
        >
          <div class="flex gap-2">
            <input
              ref={fileInput}
              type="file"
              accept=".xml,application/xml,text/xml"
              class="hidden"
              onChange={(e: Event) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) importFile(file);
              }}
            />
            <button
              class="btn btn-ghost btn-sm"
              disabled={importing.value}
              title={t("inventory.purchases.import_help")}
              onClick={() => fileInput.value?.click()}
            >
              <span class="material-symbols-outlined text-base">
                upload_file
              </span>
              {t("inventory.purchases.import")}
            </button>
            <button
              class="btn btn-primary btn-sm"
              onClick={() => (scanning.value = true)}
            >
              <span class="material-symbols-outlined text-base">
                document_scanner
              </span>
              {t("inventory.scan.scan_delivery")}
            </button>
            <button
              class="btn btn-neutral btn-sm"
              onClick={() => {
                initial.value = null;
                editing.value = null;
              }}
            >
              <span class="material-symbols-outlined text-base">add</span>
              {t("inventory.purchases.new_receipt")}
            </button>
          </div>
        </SubpageHeader>

        <div class="flex-1 overflow-auto p-3">
          {loading.value ? (
            <div class="flex h-full items-center justify-center">
              <Loading />
            </div>
          ) : receipts.value.length === 0 ? (
            <div class="flex h-full flex-col items-center justify-center gap-2 text-center">
              <span class="material-symbols-outlined text-5xl text-base-content/30">
                receipt_long
              </span>
              <p class="text-sm text-base-content/60">
                {t("inventory.purchases.empty")}
              </p>
            </div>
          ) : (
            <div class="overflow-x-auto rounded-lg border border-base-300 bg-base-100">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>{t("inventory.purchases.received_on")}</th>
                    <th>{t("inventory.purchases.supplier")}</th>
                    <th>{t("inventory.purchases.reference")}</th>
                    <th class="text-right">
                      {t("inventory.purchases.col_lines")}
                    </th>
                    <th class="text-right">
                      {t("inventory.purchases.col_total")}
                    </th>
                    <th>{t("inventory.purchases.col_status")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {receipts.value.map((r) => (
                    <tr
                      key={r.id}
                      class="cursor-pointer hover:bg-base-200/60"
                      onClick={() => (editing.value = r.id)}
                    >
                      <td>{formatDate(r.received_at)}</td>
                      <td>
                        {r.supplier_name ?? (
                          <span class="text-base-content/40">—</span>
                        )}
                      </td>
                      <td>
                        {r.reference ?? (
                          <span class="text-base-content/40">—</span>
                        )}
                        <div class="mt-0.5 flex gap-1">
                          {r.purchase_order_id && (
                            <span class="badge badge-ghost badge-xs">
                              {t("inventory.purchases.from_order")}
                            </span>
                          )}
                          {r.supplier_invoice_id && (
                            <span class="badge badge-success badge-outline badge-xs">
                              {t("inventory.purchases.invoiced")}
                            </span>
                          )}
                        </div>
                      </td>
                      <td class="text-right">{r.line_count}</td>
                      <td class="text-right font-medium">
                        {r.total.toFixed(2)}
                      </td>
                      <td>
                        <span
                          class={
                            r.status === "posted"
                              ? "badge badge-success badge-sm"
                              : "badge badge-ghost badge-sm"
                          }
                        >
                          {t(`inventory.purchases.status_${r.status}`)}
                        </span>
                      </td>
                      <td class="text-right">
                        {r.status === "draft" && (
                          <button
                            class="btn btn-ghost btn-xs"
                            onClick={(e: MouseEvent) => {
                              e.stopPropagation();
                              remove(r);
                            }}
                          >
                            <span class="material-symbols-outlined text-base">
                              delete
                            </span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {scanning.value && (
          <ScanDialog
            kind="receipt"
            onClose={() => (scanning.value = false)}
            onOpen={onOpen}
          />
        )}
        {editing.value !== undefined && (
          <ReceiptEditor
            receiptId={editing.value}
            initial={initial.value}
            onClose={async (changed: boolean) => {
              editing.value = undefined;
              initial.value = null;
              if (changed) await load();
            }}
          />
        )}
      </div>
    );
  },
});
